import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { formatCardExpiry } from "@/utils/format.util";
import type { Profile } from "../data/profile";
import type { OpenProfileOverlay } from "../overlays/profileOverlay";
import {
    CardCopyButton,
    FieldRow,
    MissingRow,
    ProfileParagraph,
    ProfileSection,
} from "../profilePrimitives";
import { listMissingLabels } from "../profileUtils";

export function PaymentsSection({
    payments,
    setOverlay,
}: {
    payments: Profile["payments"];
    setOverlay: OpenProfileOverlay;
}) {
    const [cardsOpen, setCardsOpen] = useState(false);

    useEffect(() => {
        setCardsOpen(false);
    }, [payments]);

    return (
        <ProfileSection
            title="Payments"
            editable
            onEdit={() => setOverlay({ type: "edit", section: "payments" })}
        >
            {payments.paymentsMustKnow && (
                <ProfileParagraph>{payments.paymentsMustKnow}</ProfileParagraph>
            )}
            {payments.preferredCurrency && (
                <FieldRow label="Preferred currency">
                    <p className="text-xs text-zinc-900 leading-4">
                        {payments.preferredCurrency}
                    </p>
                </FieldRow>
            )}
            {payments.invoiceNameAliases?.length ? (
                <FieldRow label="Invoice names">
                    <div className="flex flex-col gap-1">
                        {payments.invoiceNameAliases.map((name) => (
                            <p key={name} className="text-xs text-zinc-900 leading-4">
                                {name}
                            </p>
                        ))}
                    </div>
                </FieldRow>
            ) : null}
            {payments.stripe && (
                <FieldRow label="Stripe">
                    <div className="flex flex-col gap-1.5">
                        <p className="text-xs text-zinc-900 leading-4">
                            {payments.stripe.id}
                        </p>
                        {!cardsOpen ? (
                            <Button
                                variant="ghost"
                                className="h-auto justify-start px-0 py-0.5 text-2xs"
                                onClick={() => setCardsOpen(true)}
                            >
                                Reveal cards
                            </Button>
                        ) : payments.cards?.length ? (
                            payments.cards.map((card) => (
                                <CardCopyButton
                                    key={card.last4}
                                    brand={card.brand}
                                    last4={card.last4}
                                    expiry={formatCardExpiry(
                                        card.expMonth,
                                        card.expYear,
                                    )}
                                />
                            ))
                        ) : (
                            <p className="text-xs text-zinc-500 leading-4">
                                No cards added
                            </p>
                        )}
                    </div>
                </FieldRow>
            )}
            <MissingRow
                labels={listMissingLabels([
                    {
                        label: "Must know",
                        present: !!payments.paymentsMustKnow,
                    },
                    {
                        label: "Preferred currency",
                        present: !!payments.preferredCurrency,
                    },
                    { label: "Stripe", present: !!payments.stripe },
                ])}
            />
        </ProfileSection>
    );
}
